import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import { 
  ArrowLeft, Check, Bot, Zap, CreditCard, Sparkles, Crown
} from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from '../translations';
import LanguageToggle from './LanguageToggle';
import { API_URL } from '../utils/api';

const Plans = () => {
  const navigate = useNavigate();
  const { language, isRTL } = useLanguage();
  const { user } = useAuth();
  const { t } = useTranslation(language);
  
  const [loading, setLoading] = useState(true);
  const [plans, setPlans] = useState([]);
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [wallet, setWallet] = useState(null);
  const [purchasing, setPurchasing] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString(language === 'fa' ? 'fa-IR' : 'en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [plansRes, subRes, walletRes] = await Promise.all([
        axios.get(`${API_URL}/billing/plans`),
        axios.get(`${API_URL}/billing/subscription`),
        axios.get(`${API_URL}/billing/wallet`)
      ]);

      setPlans(plansRes.data.plans);
      setCurrentSubscription(subRes.data.subscription);
      setWallet(walletRes.data.wallet);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error(t('plans.fetchError') || 'Failed to load plans');
    } finally {
      setLoading(false);
    }
  };

  const handlePurchase = (plan) => {
    setSelectedPlan(plan);
    setShowConfirmModal(true);
  };

  const confirmPurchase = async () => {
    if (!selectedPlan) return;

    try {
      setPurchasing(true);
      
      await axios.post(`${API_URL}/billing/subscribe`, {
        planId: selectedPlan.id
      });

      toast.success(t('plans.purchaseSuccess') || 'Plan purchased successfully!');
      setShowConfirmModal(false);
      setSelectedPlan(null);
      
      // Redirect to wallet page
      setTimeout(() => {
        navigate('/wallet');
      }, 1500);
    } catch (error) {
      console.error('Error purchasing plan:', error);
      const errorData = error.response?.data;
      
      if (errorData?.error === 'Insufficient balance') {
        toast.error(
          `${t('plans.insufficientBalance') || 'Insufficient balance'}. ${t('plans.needMore') || 'You need'} ${formatCurrency(errorData.deficit)} ${language === 'fa' ? 'ریال بیشتر' : 'IRR more'}`
        );
        
        // Redirect to wallet for deposit
        setTimeout(() => {
          navigate('/wallet');
        }, 2000);
      } else {
        toast.error(errorData?.error || t('plans.purchaseError') || 'Purchase failed');
      }
    } finally {
      setPurchasing(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat(language === 'fa' ? 'fa-IR' : 'en-US').format(amount);
  };

  const getPlanIcon = (planName) => {
    switch (planName.toLowerCase()) {
      case 'free':
        return <Bot className="w-8 h-8" />;
      case 'starter':
        return <Zap className="w-8 h-8" />;
      case 'professional':
        return <Sparkles className="w-8 h-8" />;
      case 'enterprise':
        return <Crown className="w-8 h-8" />;
      default:
        return <Bot className="w-8 h-8" />;
    }
  };

  const isCurrentPlan = (planId) => {
    return currentSubscription?.plan_id === planId;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-gray-200 border-t-gray-900 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen bg-gradient-to-br from-gray-50 via-white to-blue-50" dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Ultra-Glassy Header - Fixed to top */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/50 backdrop-blur-3xl border-b border-white/30 shadow-2xl shadow-gray-300/20" style={{backdropFilter: 'blur(40px) saturate(200%)', WebkitBackdropFilter: 'blur(40px) saturate(200%)'}}>
        <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-6">
          <div className="flex justify-between items-center h-16 sm:h-20">
            <div className={`flex items-center gap-2 sm:gap-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
              <button
                onClick={() => navigate('/dashboard')}
                className="p-1.5 sm:p-2 hover:bg-white/30 rounded-lg sm:rounded-xl transition-all backdrop-blur-sm"
              >
                <ArrowLeft className="w-4 h-4 sm:w-5 sm:h-5 text-gray-700" />
              </button>
              <div>
                <h1 className="text-lg sm:text-xl md:text-2xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent">
                  {t('plans.title') || 'Choose Your Plan'}
                </h1>
                <p className="text-xs sm:text-sm text-gray-600 hidden sm:block">
                  {t('plans.subtitle') || 'Select the perfect plan for your needs'}
                </p>
              </div>
            </div>
            <div className={`flex items-center gap-1 sm:gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
              <LanguageToggle />
              <Link
                to="/wallet"
                className="px-2 sm:px-3 py-1.5 sm:py-2 bg-gradient-to-r from-gray-100 to-gray-200 hover:from-gray-200 hover:to-gray-300 text-gray-900 rounded-lg sm:rounded-xl transition-all text-xs font-semibold shadow-lg"
              >
                {t('plans.viewWallet') || 'View Wallet'}
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Spacer for fixed header */}
      <div className="h-16 sm:h-20"></div>

      <main className="flex-1 max-w-7xl mx-auto px-3 sm:px-4 lg:px-6 py-6 sm:py-8 md:py-12 w-full">
        {/* Wallet Balance Banner */}
        <div className="relative bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-800 rounded-2xl sm:rounded-3xl p-4 sm:p-6 md:p-8 mb-6 sm:mb-8 text-white shadow-2xl shadow-blue-500/25 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent"></div>
          <div className="absolute top-0 right-0 w-24 h-24 sm:w-32 sm:h-32 bg-white/5 rounded-full -translate-y-12 sm:-translate-y-16 translate-x-12 sm:translate-x-16"></div>
          <div className="absolute bottom-0 left-0 w-20 h-20 sm:w-24 sm:h-24 bg-white/5 rounded-full translate-y-8 sm:translate-y-12 -translate-x-8 sm:-translate-x-12"></div>
          <div className="relative flex justify-between items-center">
            <div className="flex-1 min-w-0 text-left" style={{textAlign: language === 'fa' ? 'right' : 'left'}}>
              <p className="text-blue-100 mb-1 sm:mb-2 text-xs sm:text-sm font-medium">{t('wallet.currentBalance') || 'Your Wallet Balance'}</p>
              <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-1 sm:mb-2 bg-gradient-to-r from-white to-blue-100 bg-clip-text text-transparent break-words">
                {formatCurrency(wallet?.balance || 0)} {language === 'fa' ? 'ریال' : 'IRR'}
              </h2>
              <p className="text-blue-100/80 text-xs sm:text-sm">
                {t('wallet.lastUpdated') || 'Last updated'}: {formatDate(wallet?.updated_at)}
              </p>
            </div>
            <Link
              to="/wallet"
              className="flex-shrink-0 flex items-center gap-1 px-2 sm:px-3 py-1.5 sm:py-2 bg-white/20 backdrop-blur-sm text-white rounded-lg sm:rounded-xl font-semibold hover:bg-white/30 transition-all border border-white/20 text-xs"
            >
              <CreditCard className="w-3 h-3 sm:w-4 sm:h-4" />
              <span>{t('wallet.addFunds') || 'Add Funds'}</span>
            </Link>
          </div>
        </div>

        {/* Current Plan Banner */}
        {currentSubscription && (
          <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200/50 rounded-2xl p-4 sm:p-6 mb-6 sm:mb-8 backdrop-blur-sm shadow-lg">
            <div className={`flex items-center gap-3 sm:gap-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
              <div className="w-10 h-10 sm:w-14 sm:h-14 bg-gradient-to-br from-green-100 to-emerald-100 rounded-xl sm:rounded-2xl flex items-center justify-center shadow-lg flex-shrink-0">
                <Check className="w-5 h-5 sm:w-7 sm:h-7 text-green-600" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-green-900 text-sm sm:text-lg">
                  {t('plans.currentPlan') || 'Current Plan'}:{' '}
                  <span className="bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent font-bold">
                    {language === 'fa' ? currentSubscription.name_fa : currentSubscription.name}
                  </span>
                </p>
                <p className="text-xs sm:text-sm text-green-700 mt-1">
                  {t('plans.tokensRemaining') || 'Tokens Remaining'}:{' '}
                  <span className="font-semibold">
                    {formatCurrency(currentSubscription.tokens_limit - currentSubscription.tokens_used)} /{' '}
                    {formatCurrency(currentSubscription.tokens_limit)}
                  </span>
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Plans Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-8 sm:mb-12">
          {(plans || []).map((plan) => {
            const isCurrent = isCurrentPlan(plan.id);
            const isFree = plan.price === 0;
            const isRecommended = plan.name === 'Professional';

            return (
              <div
                key={plan.id}
                className={`relative flex flex-col bg-white/70 backdrop-blur-xl rounded-2xl sm:rounded-3xl border-2 transition-all hover:shadow-2xl hover:scale-105 ${
                  isCurrent
                    ? 'border-green-400 shadow-2xl shadow-green-500/25'
                    : isRecommended
                    ? 'border-blue-400 shadow-2xl shadow-blue-500/25'
                    : 'border-white/30 hover:border-blue-300/50 shadow-xl shadow-gray-300/10'
                }`}
              >
                {/* Recommended Badge */}
                {isRecommended && !isCurrent && (
                  <div className="absolute -top-3 sm:-top-4 left-1/2 transform -translate-x-1/2">
                    <span className="px-3 sm:px-6 py-1 sm:py-2 bg-gradient-to-r from-blue-600 to-blue-700 text-white text-xs sm:text-sm font-bold rounded-full shadow-xl shadow-blue-500/25">
                      {t('plans.recommended') || 'Recommended'}
                    </span>
                  </div>
                )}

                {/* Current Plan Badge */}
                {isCurrent && (
                  <div className="absolute -top-3 sm:-top-4 left-1/2 transform -translate-x-1/2">
                    <span className="px-3 sm:px-6 py-1 sm:py-2 bg-gradient-to-r from-green-600 to-emerald-600 text-white text-xs sm:text-sm font-bold rounded-full shadow-xl shadow-green-500/25">
                      {t('plans.current') || 'Current'}
                    </span>
                  </div>
                )}

                <div className="flex flex-col flex-1 p-4 sm:p-6 md:p-8">
                  {/* Icon */}
                  <div className={`w-16 h-16 sm:w-20 sm:h-20 rounded-2xl sm:rounded-3xl flex items-center justify-center mb-4 sm:mb-6 shadow-lg ${
                    isCurrent ? 'bg-gradient-to-br from-green-100 to-emerald-100 text-green-600' :
                    isRecommended ? 'bg-gradient-to-br from-blue-100 to-indigo-100 text-blue-600' :
                    'bg-gradient-to-br from-gray-100 to-gray-200 text-gray-600'
                  }`}>
                    {getPlanIcon(plan.name)}
                  </div>

                  {/* Plan Name */}
                  <h3 className="text-xl sm:text-2xl md:text-3xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent mb-2 sm:mb-3">
                    {language === 'fa' ? plan.name_fa : plan.name}
                  </h3>

                  {/* Price */}
                  <div className="mb-6 sm:mb-8">
                    {isFree ? (
                      <div>
                        <span className="text-2xl sm:text-3xl md:text-4xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent">
                          {language === 'fa' ? 'رایگان' : 'Free'}
                        </span>
                      </div>
                    ) : (
                      <div>
                        <span className="text-2xl sm:text-3xl md:text-4xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent break-words">
                          {formatCurrency(plan.price)}
                        </span>
                        <span className="text-gray-600 mr-1 sm:mr-2 text-sm sm:text-lg font-semibold">
                          {language === 'fa' ? 'ریال' : 'IRR'}
                        </span>
                        <p className="text-xs sm:text-sm text-gray-500 mt-1 sm:mt-2 font-medium">
                          {t('plans.perMonth') || 'per month'}
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Features */}
                  <ul className="space-y-3 sm:space-y-4 mb-6 sm:mb-8 flex-1">
                    {(plan.features || []).map((feature, index) => (
                      <li key={index} className={`flex items-start gap-2 sm:gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
                        <div className="w-5 h-5 sm:w-6 sm:h-6 bg-gradient-to-br from-green-100 to-emerald-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                          <Check className="w-3 h-3 sm:w-4 sm:h-4 text-green-600" />
                        </div>
                        <span className="text-sm sm:text-base text-gray-700 font-medium">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  {/* Action Button */}
                  {isCurrent ? (
                    <button
                      disabled
                      className="w-full px-4 sm:px-6 py-3 sm:py-4 bg-gradient-to-r from-green-100 to-emerald-100 text-green-700 font-bold rounded-xl sm:rounded-2xl cursor-not-allowed border border-green-200 shadow-lg text-sm sm:text-base"
                    >
                      {t('plans.currentPlan') || 'Current Plan'}
                    </button>
                  ) : isFree ? (
                    <button
                      disabled
                      className="w-full px-4 sm:px-6 py-3 sm:py-4 bg-gradient-to-r from-gray-100 to-gray-200 text-gray-600 font-bold rounded-xl sm:rounded-2xl cursor-not-allowed border border-gray-200 shadow-lg text-sm sm:text-base"
                    >
                      {t('plans.freeIncluded') || 'Included'}
                    </button>
                  ) : (
                    <button
                      onClick={() => handlePurchase(plan)}
                      className={`w-full px-4 sm:px-6 py-3 sm:py-4 font-bold rounded-xl sm:rounded-2xl transition-all shadow-xl text-sm sm:text-base ${
                        isRecommended
                          ? 'bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white shadow-blue-500/25'
                          : 'bg-gradient-to-r from-gray-900 to-gray-800 hover:from-gray-800 hover:to-gray-700 text-white shadow-gray-500/25'
                      }`}
                    >
                      {t('plans.selectPlan') || 'Select Plan'}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Features Comparison */}
        <div className="bg-white/70 backdrop-blur-xl rounded-2xl sm:rounded-3xl border border-white/30 p-4 sm:p-6 md:p-8 shadow-xl shadow-gray-300/10">
          <h2 className="text-xl sm:text-2xl md:text-3xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent mb-4 sm:mb-6 md:mb-8 text-center">
            {t('plans.needHelp') || 'Need Help Choosing?'}
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 md:gap-8 text-center">
            <div className="bg-gradient-to-br from-blue-50/50 to-indigo-50/50 rounded-xl sm:rounded-2xl p-4 sm:p-6 border border-blue-200/30">
              <div className="w-16 h-16 sm:w-20 sm:h-20 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-2xl sm:rounded-3xl flex items-center justify-center mx-auto mb-4 sm:mb-6 shadow-lg">
                <Bot className="w-8 h-8 sm:w-10 sm:h-10 text-blue-600" />
              </div>
              <h3 className="font-bold text-gray-900 mb-2 sm:mb-3 text-base sm:text-lg">
                {t('plans.feature1Title') || 'Start Free'}
              </h3>
              <p className="text-gray-600 text-xs sm:text-sm leading-relaxed">
                {t('plans.feature1Desc') || 'Create your first chatbot for free and test all features'}
              </p>
            </div>
            <div className="bg-gradient-to-br from-green-50/50 to-emerald-50/50 rounded-xl sm:rounded-2xl p-4 sm:p-6 border border-green-200/30">
              <div className="w-16 h-16 sm:w-20 sm:h-20 bg-gradient-to-br from-green-100 to-emerald-100 rounded-2xl sm:rounded-3xl flex items-center justify-center mx-auto mb-4 sm:mb-6 shadow-lg">
                <Zap className="w-8 h-8 sm:w-10 sm:h-10 text-green-600" />
              </div>
              <h3 className="font-bold text-gray-900 mb-2 sm:mb-3 text-base sm:text-lg">
                {t('plans.feature2Title') || 'Pay As You Grow'}
              </h3>
              <p className="text-gray-600 text-xs sm:text-sm leading-relaxed">
                {t('plans.feature2Desc') || 'Upgrade when you need more bots or tokens'}
              </p>
            </div>
            <div className="bg-gradient-to-br from-purple-50/50 to-violet-50/50 rounded-xl sm:rounded-2xl p-4 sm:p-6 border border-purple-200/30">
              <div className="w-16 h-16 sm:w-20 sm:h-20 bg-gradient-to-br from-purple-100 to-violet-100 rounded-2xl sm:rounded-3xl flex items-center justify-center mx-auto mb-4 sm:mb-6 shadow-lg">
                <Sparkles className="w-8 h-8 sm:w-10 sm:h-10 text-purple-600" />
              </div>
              <h3 className="font-bold text-gray-900 mb-2 sm:mb-3 text-base sm:text-lg">
                {t('plans.feature3Title') || 'Flexible Billing'}
              </h3>
              <p className="text-gray-600 text-xs sm:text-sm leading-relaxed">
                {t('plans.feature3Desc') || 'Only pay for excess tokens when you exceed your limit'}
              </p>
            </div>
          </div>
        </div>
      </main>

      {/* Confirmation Modal */}
      {showConfirmModal && selectedPlan && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-white/90 backdrop-blur-xl rounded-3xl p-8 max-w-md w-full shadow-2xl border border-white/30">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              {t('plans.confirmPurchase') || 'Confirm Purchase'}
            </h3>
            <div className="bg-gray-50 rounded-xl p-4 mb-6">
              <div className={`flex justify-between items-center mb-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
                <span className="text-gray-600">{t('plans.plan') || 'Plan'}:</span>
                <span className="font-semibold text-gray-900">
                  {language === 'fa' ? selectedPlan.name_fa : selectedPlan.name}
                </span>
              </div>
              <div className={`flex justify-between items-center mb-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
                <span className="text-gray-600">{t('plans.price') || 'Price'}:</span>
                <span className="font-semibold text-gray-900">
                  {formatCurrency(selectedPlan.price)} {language === 'fa' ? 'ریال' : 'IRR'}
                </span>
              </div>
              <div className={`flex justify-between items-center ${isRTL ? 'flex-row-reverse' : ''}`}>
                <span className="text-gray-600">{t('plans.currentBalance') || 'Current Balance'}:</span>
                <span className={`font-semibold ${wallet?.balance >= selectedPlan.price ? 'text-green-600' : 'text-red-600'}`}>
                  {formatCurrency(wallet?.balance || 0)} {language === 'fa' ? 'ریال' : 'IRR'}
                </span>
              </div>
              {wallet?.balance < selectedPlan.price && (
                <div className="mt-3 p-3 bg-red-50 rounded-lg">
                  <p className="text-sm text-red-700">
                    {t('plans.insufficientBalanceWarning') || 'Insufficient balance. Please add funds to your wallet.'}
                  </p>
                </div>
              )}
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowConfirmModal(false);
                  setSelectedPlan(null);
                }}
                disabled={purchasing}
                className="flex-1 px-4 py-3 bg-gray-100 hover:bg-gray-200 text-gray-900 font-semibold rounded-xl transition-all disabled:opacity-50"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={confirmPurchase}
                disabled={purchasing || wallet?.balance < selectedPlan.price}
                className="flex-1 px-4 py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {purchasing ? (t('plans.purchasing') || 'Purchasing...') : (t('plans.confirm') || 'Confirm')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Plans;

